import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Autocomplete, MenuItem, TextField } from '@mui/material';
import { useLocales, useTheme } from '../../../../hooks';
import { makeStyles } from 'tss-react/mui';
import { Control, Controller, useWatch } from 'react-hook-form';
import { markAsRequired } from '../../../../utils/formHelpers';
import FormControl from '../../FormControl';
import { EnrichForm } from './Enrich';
import { useData } from '../../../../data-layer';
import { enrichTestIds } from '../../TestsIds';
import { VllChannelResponse } from '../../../../API';
import { useRecoilValue } from 'recoil';

interface DeepLinkEPGProps {
  control: Control<EnrichForm>;
  isSaving: boolean;
}

export const useStyles = makeStyles()((theme) => ({
  autocomplete: {
    minWidth: 360
  },
  option: {
    padding: `${theme.spacing(4)} !important`,
    overflow: 'hidden',
    flexWrap: 'wrap'
  },
  input: {
    width: '100%'
  }
}));

export function DeepLinkEPGField({ control, isSaving }: DeepLinkEPGProps): JSX.Element | null {
  const { classes, cx } = useStyles();
  const { formControlColor } = useTheme();
  const { t, localize } = useLocales();
  const [inputValue, setInputValue] = useState<string>();
  const [channels, setChannels] = useState<VllChannelResponse[]>([]);

  const {
    channels: {
      state: { withAllRecords, withIsFetching },
      hook: { getAll: getAllChannels }
    }
  } = useData();

  const allChannels = useRecoilValue(withAllRecords);
  const isFetchingChannels = useRecoilValue(withIsFetching);

  const selectedId = useWatch({ control, name: 'redirection.value' });
  const selectedChannel = allChannels?.find((channel) => channel.entityId === selectedId);
  const options =
    selectedChannel && !channels.some(({ entityId }) => entityId === selectedChannel.entityId)
      ? [selectedChannel, ...channels]
      : channels;

  useEffect(() => {
    if (allChannels?.length) return;
    getAllChannels();
  }, [allChannels]);

  const onValueChange = (
    event: SyntheticEvent,
    changedValue: VllChannelResponse | null,
    onChange: (value: string) => void
  ) => {
    if (event && event.type === 'click') {
      onChange(changedValue?.entityId ?? '');
    }
  };

  const onInputChange = (event: SyntheticEvent, newValue: string) => {
    if (!event) return;
    if (event.type === 'change' && newValue !== inputValue) {
      setInputValue(newValue);
      setChannels(
        allChannels?.filter(
          (channel) =>
            localize(channel.title).toLowerCase().includes(newValue.toLowerCase()) ||
            localize(channel.description).toLowerCase().includes(newValue.toLowerCase()) ||
            channel.keywords?.some((keyword) => localize(keyword).toLowerCase().includes(newValue.toLowerCase()))
        ) ?? []
      );
    } else {
      setInputValue('');
    }
  };

  return (
    <Controller
      control={control}
      rules={{ required: t('general.field_is_required') }}
      name="redirection.value"
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <FormControl>
          <Autocomplete
            role="listbox"
            options={options}
            clearOnBlur
            blurOnSelect
            loading={isFetchingChannels}
            className={classes.autocomplete}
            filterOptions={(options) => options}
            getOptionLabel={(option) => localize(option.title)}
            renderOption={(props, option) => (
              <MenuItem
                {...props}
                className={cx(props.className, classes.option)}
                key={option?.entityId || 'none'}
                data-option={option?.entityId || null}
                data-testid={enrichTestIds.deepLinkContentFieldOption}
              >
                <div>{localize(option.title)}</div>
              </MenuItem>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                className={classes.input}
                name="collection"
                label={markAsRequired(t('content.drawer.deep_link.autocomplete_label'), true)}
                color={formControlColor}
                error={!!error}
                helperText={error?.message}
              />
            )}
            value={options.find((channel) => channel.entityId === value) || null}
            onChange={(event, changedValue) => onValueChange(event, changedValue, onChange)}
            onInputChange={onInputChange}
            isOptionEqualToValue={(option, value) => option.entityId === value.entityId}
            data-testid={enrichTestIds.deepLinkContentFieldAutocomplete}
            disabled={isSaving}
          />
        </FormControl>
      )}
    />
  );
}
